import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useData } from '../contexts/DataContext.jsx';
import { Eye, EyeOff, LogIn, Shield, KeyRound, ArrowLeft, Check, Lock, Loader2 } from 'lucide-react';
import InstallPWAButton from '../components/InstallPWAButton.jsx';
import DNALogo from '../components/DNALogo.jsx';

const APP_VERSION = '2.2.0';

function FKLogoLogin() {
  return (
    <div className="flex justify-center animate-fade-in">
      <DNALogo tone="light" width={220} />
    </div>
  );
}

function ChangePasswordScreen({ onComplete, userName }) {
  const { changePassword } = useAuth();
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Política de senha forte (mesma do server pra UX consistente)
    if (newPass.length < 8) {
      setError('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (!/[a-zA-Z]/.test(newPass)) {
      setError('A senha precisa de pelo menos 1 letra.');
      return;
    }
    if (!/[0-9]/.test(newPass)) {
      setError('A senha precisa de pelo menos 1 número.');
      return;
    }
    const common = ['12345678', '123456789', 'senha123', 'password', 'qwerty123', 'mudar123', 'admin123'];
    if (common.includes(newPass.toLowerCase())) {
      setError('Senha muito comum. Escolha algo mais único.');
      return;
    }
    if (newPass !== confirmPass) {
      setError('As senhas não coincidem.');
      return;
    }
    setLoading(true);
    // currentPassword null aqui porque é primeira troca (mustChangePassword)
    const result = await changePassword(newPass, null);
    setLoading(false);
    if (result?.ok) {
      onComplete();
    } else {
      setError(result?.error || 'Erro ao alterar senha. Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#41659E]/[0.12] rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-sm animate-fade-in">
        <div className="text-center mb-10">
          <FKLogoLogin />
        </div>
        <div className="bg-[#16243A] border border-[#23344F] rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand-300/15 border border-brand-300/20 flex items-center justify-center">
              <KeyRound size={18} className="text-brand-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#E9EEF5]">Cadastre sua senha</h2>
              <p className="text-[#8C9CB5] text-xs">Olá, {userName}!</p>
            </div>
          </div>
          <p className="text-[#5B6C86] text-xs mb-6 mt-3">
            Este é seu primeiro acesso ou sua senha foi redefinida. Cadastre uma nova senha para continuar.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5">Nova senha</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={newPass}
                  onChange={e => { setNewPass(e.target.value); setError(''); }}
                  placeholder="Mín. 8 caracteres + letra + número"
                  required
                  autoFocus
                  className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B6C86] hover:text-[#8C9CB5] transition-colors p-0.5">
                  {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5">Confirmar senha</label>
              <input
                type={showPass ? 'text' : 'password'}
                value={confirmPass}
                onChange={e => { setConfirmPass(e.target.value); setError(''); }}
                placeholder="Repita a nova senha"
                required
                className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
              />
            </div>

            {error && (
              <div className="bg-red-950/40 border border-red-900/50 rounded-xl px-4 py-3 text-red-400 text-sm flex items-center gap-2">
                <Shield size={14} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !newPass || !confirmPass}
              className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm tracking-wide mt-2 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Salvando...</>
              ) : (
                <><Check size={16} /> Salvar nova senha</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null | 'success'
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      // Server-side: sempre retorna ok (não revela se email existe — anti-enum)
      await fetch('/api/auth-reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setStatus('success');
    } catch (err) {
      setStatus('success'); // mesma resposta — não vaza info
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#41659E]/[0.12] rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-sm animate-fade-in">
        <div className="text-center mb-10">
          <FKLogoLogin />
        </div>
        <div className="bg-[#16243A] border border-[#23344F] rounded-2xl p-8 shadow-2xl">
          <button onClick={onBack} className="flex items-center gap-1.5 text-[#8C9CB5] text-xs hover:text-brand-300 transition-colors mb-5">
            <ArrowLeft size={14} /> Voltar ao login
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand-300/15 border border-brand-300/20 flex items-center justify-center">
              <Lock size={18} className="text-brand-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#E9EEF5]">Esqueci minha senha</h2>
            </div>
          </div>
          <p className="text-[#5B6C86] text-xs mb-6 mt-3">
            Informe seu email cadastrado. Vamos enviar um link pra você criar uma nova senha. O link expira em 60 minutos.
          </p>

          {status === 'success' ? (
            <div className="space-y-4">
              <div className="bg-green-950/40 border border-green-900/50 rounded-xl px-4 py-4 text-green-400 text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Check size={16} />
                  <span className="font-semibold">Verifique seu email</span>
                </div>
                <p className="text-green-400/80 text-xs leading-relaxed">
                  Se esse email estiver cadastrado, você vai receber um link em alguns instantes.
                  Cheque também a caixa de spam. O link é válido por 60 minutos e só pode ser usado uma vez.
                </p>
              </div>
              <button
                onClick={onBack}
                className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all text-sm tracking-wide flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} /> Voltar ao login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5">Email cadastrado</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setStatus(null); }}
                  placeholder="seu@email.com"
                  required
                  autoFocus
                  className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm tracking-wide flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Enviando...</>
                ) : (
                  <><KeyRound size={16} /> Enviar link de redefinição</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login, mustChangePassword, user } = useAuth();
  const { cloudLoaded } = useData();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [screen, setScreen] = useState('login'); // 'login' | 'forgot' | 'change'

  useEffect(() => {
    setMounted(true);
  }, []);

  // If user logged in but must change password, show change screen
  useEffect(() => {
    if (mustChangePassword && user) {
      setScreen('change');
    }
  }, [mustChangePassword, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Bloqueia login até o cloud sync terminar — sem isso, em reinstalação
    // do PWA o localStorage começa só com seed (senhas default 'mudar123')
    // e qualquer senha real do user é rejeitada como inválida.
    if (!cloudLoaded) {
      setError('Aguarde — sincronizando dados do servidor…');
      return;
    }
    if (loading) return; // anti double-click
    setError('');
    setLoading(true);
    try {
      // ⚠️ `login` é ASYNC — sem await, result é uma Promise e .success é undefined,
      // causando "erro" falso na 1ª tentativa enquanto a request real ainda roda.
      const result = await login(email, password);
      if (!result?.success) {
        // Defesa: converte obj de erro pra string (evita React error #31 que
        // travava a app inteira quando o backend devolvia {code,id,message})
        const e = result?.error;
        const msg = typeof e === 'string' ? e
          : (e && typeof e === 'object' && (e.message || e.code)) ? (e.message || e.code)
          : 'Erro ao fazer login';
        setError(msg);
        setShake(true);
        setTimeout(() => setShake(false), 600);
      } else if (result.mustChangePassword) {
        setScreen('change');
      }
    } finally {
      setLoading(false);
    }
  };

  if (screen === 'forgot') {
    return <ForgotPasswordScreen onBack={() => setScreen('login')} />;
  }

  if (screen === 'change' && user) {
    return <ChangePasswordScreen userName={user.name} onComplete={() => setScreen('login')} />;
  }

  return (
    <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Radial glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#41659E]/[0.12] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#41659E]/[0.06] rounded-full blur-3xl" />
      </div>

      <div className={`relative w-full max-w-sm transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>

        {/* Logo */}
        <div className="text-center mb-10">
          <FKLogoLogin />
          <p className="text-[#5B6C86] text-xs mt-3 tracking-widest uppercase">Sistema de Gestao</p>
        </div>

        {/* Card */}
        <div className={`bg-[#16243A] border border-[#23344F] rounded-2xl p-8 shadow-2xl transition-transform ${shake ? 'animate-shake' : ''}`}>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-[#E9EEF5] tracking-tight">Entrar</h2>
            <p className="text-[#8C9CB5] text-sm mt-1">Acesse o sistema de gestao</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5 tracking-wide">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                placeholder="seu@email.com"
                required
                autoComplete="email"
                className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5 tracking-wide">Senha</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B6C86] hover:text-[#8C9CB5] transition-colors p-0.5"
                >
                  {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-950/40 border border-red-900/50 rounded-xl px-4 py-3 text-red-400 text-sm flex items-center gap-2">
                <Shield size={14} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !cloudLoaded || !email || !password}
              className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm tracking-wide mt-2 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verificando...
                </>
              ) : !cloudLoaded ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Sincronizando…
                </>
              ) : (
                <>
                  <LogIn size={16} />
                  Entrar
                </>
              )}
            </button>
          </form>

          {/* Forgot password */}
          <div className="mt-4 text-center">
            <button
              onClick={() => setScreen('forgot')}
              className="text-[#5B6C86] text-xs hover:text-brand-300 transition-colors"
            >
              Esqueci minha senha
            </button>
          </div>

          {/* CTA: instalar como PWA (só aparece em mobile e quando aplicável) */}
          <InstallPWAButton />

        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-[#5B6C86] text-xs tracking-wide">
            Feel King Barbearia &copy; {new Date().getFullYear()}
          </p>
          <p className="text-[#34445E] text-[10px] tracking-wider">
            v{APP_VERSION}
          </p>
        </div>
      </div>
    </div>
  );
}
